import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useEscapeKey, useFocusTrap, useLockBodyScroll } from "../../hooks";
import { buildImageUrl } from "../../lib/imageSource";
import "./Lightbox.css";

/** Minimum horizontal travel, in pixels, that counts as a swipe. */
const SWIPE_THRESHOLD_PX = 48;

/**
 * Fullscreen image viewer for a project's gallery.
 *
 * This replaces `react-image-gallery`, which shipped its own visual language
 * — rounded thumbnails, chunky chevrons, a light theme — that fought the rest
 * of the site. A bespoke viewer is about ninety lines and gives us the things
 * the dependency did not: a focus trap, arrow-key navigation, a swipe
 * gesture, an accurate counter, and neighbouring-image preloading so paging
 * never shows a blank frame.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {string[]} props.images        Absolute image URLs.
 * @param {number} props.startIndex      Index to open on.
 * @param {string} props.projectName     Used for alt text and the label.
 * @param {() => void} props.onClose
 * @returns {JSX.Element | null}
 */
function Lightbox({ isOpen, images, startIndex, projectName, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const touchStartXRef = useRef(null);

  useLockBodyScroll(isOpen);
  useEscapeKey(isOpen, onClose);
  const lightboxRef = useFocusTrap(isOpen);

  // Re-sync when the viewer is reopened from a different thumbnail.
  useEffect(() => {
    if (isOpen) setCurrentIndex(startIndex);
  }, [isOpen, startIndex]);

  const totalImageCount = images.length;

  const goToPrevious = useCallback(() => {
    setCurrentIndex(
      (previousIndex) => (previousIndex - 1 + totalImageCount) % totalImageCount
    );
  }, [totalImageCount]);

  const goToNext = useCallback(() => {
    setCurrentIndex((previousIndex) => (previousIndex + 1) % totalImageCount);
  }, [totalImageCount]);

  /* ----------------------------------------------- arrow-key navigation */
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "ArrowLeft") {
        keyboardEvent.preventDefault();
        goToPrevious();
      } else if (keyboardEvent.key === "ArrowRight") {
        keyboardEvent.preventDefault();
        goToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goToPrevious, goToNext]);

  /* -------------------------------------------------------- swipe support */
  const handleTouchStart = (touchEvent) => {
    touchStartXRef.current = touchEvent.touches[0].clientX;
  };

  const handleTouchEnd = (touchEvent) => {
    const startX = touchStartXRef.current;
    if (startX === null) return;

    const horizontalTravel = touchEvent.changedTouches[0].clientX - startX;
    touchStartXRef.current = null;

    if (Math.abs(horizontalTravel) < SWIPE_THRESHOLD_PX) return;
    // Dragging left (negative travel) advances, matching native photo viewers.
    if (horizontalTravel < 0) goToNext();
    else goToPrevious();
  };

  if (!isOpen || totalImageCount === 0) return null;

  /*
    The viewer fills the window, so every frame is requested at a single large
    size rather than through a srcset — and crucially the preloaders must
    request the SAME URL the visible image will use, or the preload is wasted.
  */
  const LIGHTBOX_WIDTH = 2048;
  const toViewerUrl = (url) => buildImageUrl(url, { width: LIGHTBOX_WIDTH });

  const previousImageUrl = toViewerUrl(
    images[(currentIndex - 1 + totalImageCount) % totalImageCount]
  );
  const nextImageUrl = toViewerUrl(images[(currentIndex + 1) % totalImageCount]);

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Galerija — ${projectName}`}
      ref={lightboxRef}
      tabIndex={-1}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Clicking the backdrop dismisses. The buttons below stop propagation
          implicitly by sitting above it in the stack. */}
      <button
        type="button"
        className="lightbox__backdrop"
        onClick={onClose}
        tabIndex={-1}
        aria-hidden="true"
      />

      <div className="lightbox__stage">
        <img
          className="lightbox__image"
          /* Keying by URL remounts the element on change, so the fade-in
             animation replays for each photograph. */
          key={images[currentIndex]}
          src={toViewerUrl(images[currentIndex])}
          alt={`${projectName} — vizualizacija ${currentIndex + 1} od ${totalImageCount}`}
          decoding="async"
          onError={(event) => {
            // Fall back to the untouched original if the CDN URL fails.
            if (event.currentTarget.src !== images[currentIndex]) {
              event.currentTarget.src = images[currentIndex];
            }
          }}
        />
      </div>

      {/* Hidden preloaders for the adjacent frames */}
      <div className="lightbox__preload" aria-hidden="true">
        <img src={previousImageUrl} alt="" decoding="async" />
        <img src={nextImageUrl} alt="" decoding="async" />
      </div>

      {/* --------------------------------------------------------- controls */}
      <div className="lightbox__bar lightbox__bar--top">
        <p className="lightbox__title t-label">{projectName}</p>
        <button type="button" className="lightbox__close" onClick={onClose}>
          <span>Zatvori</span>
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="1.25" />
          </svg>
        </button>
      </div>

      <div className="lightbox__bar lightbox__bar--bottom">
        <p className="lightbox__counter t-numeral">
          <span className="lightbox__counter-current">
            {String(currentIndex + 1).padStart(2, "0")}
          </span>
          <span className="lightbox__counter-divider" aria-hidden="true">
            /
          </span>
          <span className="lightbox__counter-total">
            {String(totalImageCount).padStart(2, "0")}
          </span>
        </p>

        <div className="lightbox__nav">
          <button
            type="button"
            className="lightbox__step"
            onClick={goToPrevious}
            aria-label="Prethodna slika"
          >
            <svg viewBox="0 0 24 16" aria-hidden="true" focusable="false">
              <path
                d="M8 1L1 8l7 7M1 8h23"
                stroke="currentColor"
                strokeWidth="1.25"
                fill="none"
              />
            </svg>
          </button>
          <button
            type="button"
            className="lightbox__step"
            onClick={goToNext}
            aria-label="Sljedeća slika"
          >
            <svg viewBox="0 0 24 16" aria-hidden="true" focusable="false">
              <path
                d="M16 1l7 7-7 7M23 8H0"
                stroke="currentColor"
                strokeWidth="1.25"
                fill="none"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Announce the change to screen reader users without stealing focus */}
      <p className="visually-hidden" aria-live="polite">
        Slika {currentIndex + 1} od {totalImageCount}
      </p>
    </div>,
    document.body
  );
}

export default Lightbox;
